/* eslint-disable unicorn/no-null */
/* eslint-disable @typescript-eslint/no-magic-numbers */
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponseHeaders,
  Canceler,
  RawAxiosResponseHeaders,
} from 'axios';

import { ROUTES } from '@/AppRoutes';
import Constants from '@/Constants';
import { LevelStatus } from '@/pages/MissionSelection/components/LevelList';
import {
  EngagementStrategyStatusEnum,
  MissionStatusEnum,
  StageStatusEnum,
} from '@/pages/TaskPlayer/types';
import { Question } from '@/slices/testSlice';
import useStore from '@/store';
import { getSessionItem, isApiResponseSuccess, isDesktop, isLinux } from '@/utils/helper';

// Define types for API responses
type GenericResponse<T> = {
  data: T;
  status: number;
  statusText: string;
  headers: AxiosResponseHeaders | RawAxiosResponseHeaders;
  config: AxiosRequestConfig;
};
type ResponseWithErrorInfo<ResponseType> = ResponseType extends { code?: number; message?: string }
  ? ResponseType
  : ResponseType & { code?: number; message?: string };
export type GenericApiResponse<T> = GenericResponse<ResponseWithErrorInfo<T>>;

const beforeStudentLoginRoutes = new Set([
  Constants.GET_STUDENT_BY_ACCESS_TOKEN_URL,
  Constants.VERIFY_AT_HOME_ACCESS_CODE_URL,
  Constants.SEND_OTP_URL,
  Constants.VERIFY_OTP_URL,
  Constants.RESET_APP_URL,
  Constants.GET_STUDENT_BY_MOBILE,
  Constants.STUDENT_REFRESH_TOKEN_URL,
  Constants.GET_STUDENTS_URL,
  Constants.GET_GRADES_URL,
  Constants.CHECK_PASSWORD_URL,
  Constants.LOGIN_STUDENT_URL,
  Constants.GET_RESET_PASSWORD_TOKEN_URL,
  Constants.RESET_PASSWORD_URL,
  Constants.GET_STUDENT_DETAILS,
  Constants.SET_STUDENT_PASSWORD_URL,
  Constants.VALIDATE_CENTER_URL,
  Constants.ADMIN_LOGIN_URL,
  Constants.REFRESH_TOKEN_URL,
  Constants.ADMIN_LOGOUT_URL,
  Constants.GET_ADMIN_DETAILS,
  Constants.SUBMIT_ADMIN_FEEDBACK,
  Constants.RESET_APP,
]);

const adminOnlyRoutes = new Set([
  Constants.SUBMIT_ADMIN_FEEDBACK,
  Constants.GET_ADMIN_DETAILS,
  Constants.RESET_APP,
  Constants.ADMIN_LOGOUT_URL,
  Constants.GET_OFFLINE_ZIP_UPDATES_URL,
  Constants.GET_OFFLINE_ZIP_UPDATE_DETAILS_URL,
  Constants.ADMIN_GET_SUBJECT_LIST_URL,
  Constants.ADMIN_GENERATE_CODE_URL,
]);

export type ValidateCenterResponse = {
  adminId: string;
  centerName: string;
  centerCode: string;
  programCode: string;
  isFln: boolean;
  splashScreenImage: {
    left: string;
    right: string;
  };
  accessToken: {
    token: string;
    expirationTime: number;
  };
  refreshToken: string;
};

export type RefreshTokeResponse = {
  accessToken: {
    token: string;
    expirationTime: number;
  };
};

export type Grade = {
  id: string;
  name: string;
};

export type Student = {
  id: string;
  name: string;
  code: string;
  avatar?: string | null;
};

export type Password = {
  fruit: string;
  animal: string;
};

export type GradesResponse = {
  grades: Grade[];
};

export type StudentResponse = {
  students: Student[];
};

export type AdminLoginResponse = {
  sessionId: string;
  serverTime?: number;
  code?: number;
  message?: string;
};

export type StudentInfoResponse = {
  profile: {
    name: string;
    code: string;
    grade: string;
    centerName: string;
  };
  stats: {
    levelsCompleted: number;
    missionsCompleted: number;
  };
  code?: number;
  message?: string;
};

export type AdminFeedbackParameters = {
  mobile: string;
  email: string;
  category: string;
  description: string;
  platform: string;
  deviceId: string;
  appVersion: string;
  deviceMetadata: {
    model: string | undefined;
    operatingSystem: string | undefined;
    browser?: string | undefined;
  };
};

export type StudentFeedbackParameters = {
  category: string;
  description: string;
  adminId: string | null;
  appVersion: string;
  deviceId: string;
  platform: string;
  deviceMetadata: {
    model: string | undefined;
    operatingSystem: string | undefined;
    browser?: string | undefined;
  };
};

export type GetDetailsResponse = {
  id: string;
  name: string;
  code: string;
  metadata: {
    activityEnabled: boolean;
    coinsAvailable: boolean;
    nudgesEnabled: boolean;
    description: string;
    splashScreenImage: {
      left: string;
      right: string;
    };
    contact: {
      phone: string;
      email: string;
    };
    assessmentStartTime: {
      hour: number;
      minute: number;
    };
    assessmentEndTime: {
      hour: number;
      minute: number;
    };
    moduleCompletionThreshold: number;
    allowHomeLearning?: boolean;
    desktopConfig: {
      allowOnlineStreaming: boolean;
      offlineStreamingAvailable: boolean;
    };
    webConfig: {
      allowOnlineStreaming: boolean;
      offlineStreamingAvailable: boolean;
    };
    androidConfig: {
      allowOnlineStreaming: boolean;
      offlineStreamingAvailable: boolean;
    };
    diagnosticAccessType?: string;
  };
};

export type SetStudentPasswordResponse = {
  student: {
    name: string;
    code: string;
  };
  sessionId: string;
  code?: number;
  message?: string;
};

export type SetStudentResponseData = { name: string; code: string } | null;

export type StudentLoginResponse = {
  student: {
    name: string;
    code: string;
  };
  sessionId: string;
  coinBalance?: number;
  code?: number;
  message?: string;
};

export type CheckPasswordResponse = {
  hasPassword: boolean;
};

export type ResetPasswordTokenResponse = {
  resetPasswordToken?: string;
  code?: number;
  message?: string;
};

export type ResetPasswordResponse = {
  resetPasswordToken: string;
  password: string;
  studentId: string;
  code?: number;
  message?: string;
};

export type StudentFeedbackResponse = {
  ticketId: string;
  code?: number;
  message?: string;
};

export type GetAtHomeAccessCodeResponse = {
  accessCode: string;
  accessCodeExpiryTime: string;
  code?: number;
  message?: string;
};

export type LeaderboardEntry = {
  rank: number;
  studentId: string;
  name: string;
  coins: number;
};

export type LeaderboardCurrentUser = {
  rank: number;
  coins: number;
  name: string;
};

export type LeaderboardResponse = {
  currentUser: LeaderboardCurrentUser;
  leaderboard: LeaderboardEntry[];
  code?: number;
  message?: string;
};

export type ErrorCodeResponse = {
  code?: number;
  message?: string;
};

export interface GetStudentCardsResponse {
  cardMap: {
    [key: string]: {
      name: string;
      thumbnail: string;
    };
  };
}

export interface GetMonsterCardsResponse {
  cardMap: {
    [key: string]: {
      monsterName: string;
      monsterImage: string;
    };
  };
}

export interface StudentMonsterCard {
  loId: string;
  monsterName: string;
  monsterImage: string;
  type: string;
}

export interface GetStudentMonsterCardsResponse {
  monsterCardList: StudentMonsterCard[];
}

export interface StudentSubjectCard {
  subjectId: string;
  name: string;
  thumbnail: string;
}

export interface GetStudentSubjectCardsResponse {
  subjectCardList: StudentSubjectCard[];
}

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token or admin ID available');
  }

  const accessLocation = localStorage.getItem('accessLocation');

  const url =
    accessLocation === 'home' ? Constants.STUDENT_REFRESH_TOKEN_URL : Constants.REFRESH_TOKEN_URL;

  try {
    const response = await axios.post<RefreshTokeResponse>(
      url,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json', 'access-location': accessLocation } },
    );

    localStorage.setItem('accessToken', response.data.accessToken.token);
    // localStorage.setItem('refreshToken', response.data.refreshToken);

    return response.data.accessToken.token;
  } catch (error) {
    const axiosError = error as AxiosError<{ code: number; message: string }>;
    if (axiosError.response?.data?.code === 15) {
      const deviceId = localStorage.getItem('device-id');
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = ROUTES.ROOT;
      if (deviceId) {
        localStorage.setItem('device-id', deviceId);
      }
    }
    throw error;
  }
};

// eslint-disable-next-line @typescript-eslint/ban-types
export type AdminLogoutResponse = {};

export type GetOfflineZipUpdatesResponse = {
  updates: {
    id: string;
    version: number;
    size: number;
    createdAt: string;
    sha256: string;
  }[];
  code?: number;
  message?: string;
};

export type GetOfflineZipUpdateDetailsResponse = {
  id: string;
  version: number;
  size: number;
  path: string;
  signedCookies?: Record<string, string>;
  code?: number;
  message?: string;
};

export type AdminSubject = {
  id: string;
  name: string;
  subjectNo: number;
  thumbnail: string;
  medium: string;
  type?: 'ADAPTIVE' | 'NON_ADAPTIVE';
};

export type AdminSubjectListResponse = {
  subjectList: AdminSubject[];
  subjectTranslations: SubjectTranslation;
  code?: number;
  message?: string;
};

export type GenerateTestCodeRequest = {
  subjectId: string;
  type: 'access' | 'abort';
};

export type GenerateTestCodeResponse = {
  code: number;
  validTill: string;
  message?: string;
};

export type Subject = {
  id: string;
  name: string;
  subjectNo: number;
  currentLevelId: string | null;
  thumbnail: string;
  medium: string;
  /** Defaults to adaptive behavior when omitted. */
  type?: 'ADAPTIVE' | 'NON_ADAPTIVE';
};

export type SubTranslation = {
  name: string;
  thumbnail: string;
};

export type SubjectTranslation = {
  [key: string]: SubTranslation;
};

export type SubjectResponse = {
  subjectList: Subject[];
  subjectTranslations: SubjectTranslation;
  code?: number;
  message?: string;
};

export type InstructionsResponse = {
  studentName: string;
  instructions: [];
  maxAllottedTime: number;
  youtubeVideoLink?: string;
  hlsVideoLink?: string;
};

export type CheckAvailabilityResponse = {
  assessmentId: string;
  assessmentType: string;
  isAdaptive: boolean;
  attemptDetails: {
    attemptId: string;
  };
  code?: number;
  message?: string;
};

export type InitiateTestResponse = {
  attemptId: string;
  endTime: string;
  question: Question;
  code?: number;
  message?: string;
  questionCount: number;
};

export type RejoinResponse = {
  endTime: string;
  question: Question;
  questionCount: number;
  code?: number;
  message?: string;
};

export type SubmitResponse = {
  attemptId: string;
  question: {
    id: string;
    text: string;
    media: string;
    options: [{ type: string; value: string }];
  };
  questionCount: number;
};

export type AbortTestResponse = {
  code?: number;
  message?: string;
};

export type AssesementSubmitResponse = {
  isCompleted: boolean;
  code?: number;
  message?: string;
};

export type Level = {
  id: string;
  levelNo: number;
  status: LevelStatus;
};

export type GetLevelListResponse = {
  levelList: Level[];
  currentLevelId: string;
  code?: number;
  message?: string;
};

export type Mission = {
  id: string;
  loId: string;
  status: MissionStatusEnum;
  missionNo: number;
  missionType: string;
  skillCount: number;
  skillCompleted: number;
};

export type Lo = {
  id: string;
  name: string;
  monsterName: string;
  monsterImage: string;
};

export type Translation = {
  monsterName: string;
  name: string;
};

export type LoTranslations = {
  [key: string]: Translation;
};

export type GetMissionListResponse = {
  missionList: Mission[];
  loList: Lo[];
  loTranslations: LoTranslations;
};

export type Stage = {
  id: string;
  skillId: string;
  stageNo: number;
  status: StageStatusEnum;
  totalStages: number;
  missionAttemptNo: number;
  engagementStrategyStatus: EngagementStrategyStatusEnum;
  engagementStrategyModuleCount: number;
  engagementStrategyCompletedModuleCount: number;
};

export type Skill = {
  id: string;
  description: string;
};

export enum ModuleTypeEnum {
  video = 'VIDEO',
  pdf = 'PDF',
  onlineHtml = 'ONLINE_HTML',
  offlineHtml = 'OFFLINE_HTML',
  quiz = 'QUIZ',
  youtubeLink = 'YOUTUBE_LINK',
}

export type Module = {
  id: string;
  path: string;
  seqNo: number;
  moduleType: ModuleTypeEnum;
  moduleCategory: string;
  metadata?: {
    hasEmbeddedEvents?: boolean;
  };
};

export type TranslationObject = {
  id: string;
  description: string;
};

export type SkillTranslation = {
  [key: string]: TranslationObject;
};

export type GetStageListResponse = {
  stageList: Stage[];
  skillList: Skill[];
  currentStageId: string;
  missionAttemptNo: number | null;
  skillTranslations: SkillTranslation;
  code?: number;
  message?: string;
};

export type AdminFeedbackResponse = {
  code?: number;
  message?: string;
  ticketId: string;
};

export type PostContentReportResponse = {
  ticketId?: string;
  code?: number;
  message?: string;
};

export type MetadataBase = {
  skillDescription?: string;
  modulePath?: string;
  timestamp?: number;
  quizType?: string;
  cmsQuestionId?: string;
};

export type PostContentReportParameters = {
  programCode: string;
  studentCode: string;
  subjectName: string;
  gradeName: string;
  medium: string;
  moduleType: string;
  problemDescriptionTag: string;
  problemDescription?: string;
  metadata: MetadataBase;
};

export type GetModuleListResponse = {
  moduleList: Module[];
  lastCompletedModuleId: string;
  stageAttemptNo: number;
};

export type AtHomeLoginResponse = {
  accessToken: {
    token: string;
    expirationTime: number;
  };
  refreshToken: string;
  refreshTokenExpiryTime: string;
  splashScreenImage: {
    left: string;
    right: string;
  };
};

export type ResetAppResponse = {
  code?: number;
  message?: string;
};

export type SendOtpResponse = {
  code?: number;
  message?: string;
};

export type VerifyOtpParameters = {
  mobile: string;
  otp: string;
};

export type AtHomeStudent = {
  id: string;
  name: string;
  code: string;
  gradeId: string;
  gradeName: string;
};

export type FollowUpQuestionOption = {
  value: string;
  isCorrect: boolean;
};

export type GetFollowUpQuestionResponse = {
  questionText: string;
  options: FollowUpQuestionOption[];
};

export type GetStudentByAccessTokenResponse = {
  students: AtHomeStudent[];
  code?: number;
  message?: string;
};

export type CarrotSignedCookies = {
  'CloudFront-Key-Pair-Id': string;
  'CloudFront-Signature': string;
  'CloudFront-Policy': string;
};

export type CarrotContent = {
  encryptedKey: string | null;
  signedCookies: CarrotSignedCookies;
} | null;

export type CarrotType = 'OFFLINE_HTML' | 'ONLINE_HTML';

export type Carrot = {
  id: string;
  path: string;
  type: CarrotType;
  content: CarrotContent;
  duration: number | null;
};

export type GetCarrotResponse = {
  carrot: Carrot | null;
};

export type CarrotStatsEvent = 'claimed' | 'skipped' | 'closed';

export type CarrotStatsParameters = {
  carrotId: string;
  event: CarrotStatsEvent;
  missionId: string;
};

export type Certificate = {
  id: string;
  activityId: string;
  studentName: string;
  activityName: string;
  issuedAt: string;
};

export type GetCertificatesResponse = {
  certificates: Certificate[];
  code?: number;
  message?: string;
};

export const makeRequest = async <ResponseType>(
  url: string,
  data: Record<string, unknown> = {},
  extraHeaders: Record<string, unknown> = {},
  method: 'get' | 'post' = 'post',
): Promise<GenericApiResponse<ResponseType>> => {
  let cancelFunction: Canceler;
  const accessToken = localStorage.getItem('accessToken');
  const { appVersion } = useStore.getState();
  let packageNamePrefix: 'linux' | 'desktop' | 'pwa';
  if (!isDesktop()) {
    packageNamePrefix = 'pwa';
  } else if (isLinux()) {
    packageNamePrefix = 'linux';
  } else {
    packageNamePrefix = 'desktop';
  }
  const { getCurrentTime, serverTime, performanceReference, performanceTimeoriginReference } =
    useStore.getState();

  const timeLog = {
    currentTime: getCurrentTime(),
    serverTime,
    performanceReference,
    performanceTimeoriginReference,
    'performance.now()': performance.now(),
    'performance.timeOrigin': performance.timeOrigin,
  };

  const headers = {
    ...(url.startsWith(Constants.AUTH_BASE_URL) && { 'Content-Type': 'application/json' }),
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    'access-location': localStorage.getItem('accessLocation'),
    'package-name': `${packageNamePrefix}-ai.convegenius.pal.${Constants.ENVIRONMENT}`,
    'version-name': `${appVersion}/${Constants.GIT_INFO}`,
    'time-log': JSON.stringify(timeLog),
    'version-code': `${Constants.APP_VERSION}`,
    ...extraHeaders,
    'device-id': localStorage.getItem('device-id'),
    'app-language': useStore.getState().appLanguage,
  };

  const config: AxiosRequestConfig = {
    method,
    url,
    headers,
    data,
    cancelToken: new axios.CancelToken((cancel) => {
      cancelFunction = cancel;
    }),
  };

  const timeoutId = setTimeout(() => {
    if (cancelFunction) {
      cancelFunction('Request timed out');
    }
  }, Constants.CONNECTION_TIMEOUT);

  try {
    const response = await axios<ResponseWithErrorInfo<ResponseType>>(config);
    clearTimeout(timeoutId);
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      config: response.config,
    };
  } catch (error: unknown) {
    clearTimeout(timeoutId);

    if (axios.isCancel(error)) {
      throw new Error('Request cancelled or timed out');
    }

    const axiosError = error as AxiosError<ResponseWithErrorInfo<ResponseType>>;

    if (axiosError.response?.status === 401) {
      if (!beforeStudentLoginRoutes.has(url)) {
        useStore.getState().logoutStudent({ source: 'api' });
      } else if (adminOnlyRoutes.has(url)) {
        useStore.getState().clearCurrentAdminSession();
      } else if (localStorage.getItem('refreshToken')) {
        try {
          const newAccessToken = await refreshAccessToken();
          config.headers = { ...config.headers, Authorization: `Bearer ${newAccessToken}` };
          const retryResponse = await axios<ResponseWithErrorInfo<ResponseType>>(config);
          return {
            data: retryResponse.data,
            status: retryResponse.status,
            statusText: retryResponse.statusText,
            headers: retryResponse.headers,
            config: retryResponse.config,
          };
        } catch {
          throw new Error('Failed to refresh token');
        }
      }
    }

    if (
      axiosError.response && // Handle 4xx status codes
      Math.floor(axiosError.response.status / 100) === 4
    ) {
      return {
        data: axiosError.response.data,
        status: axiosError.response.status,
        statusText: axiosError.response.statusText,
        headers: axiosError.response.headers,
        config: axiosError.response.config,
      };
    }

    throw error;
  }
};

const apiClient = {
  validateCenter: async ({
    adminCode,
    password,
    programCode,
  }: {
    adminCode: string;
    password: string;
    programCode: string;
  }): Promise<GenericApiResponse<ValidateCenterResponse> | null> => {
    const result = await makeRequest<ValidateCenterResponse>(Constants.VALIDATE_CENTER_URL, {
      adminCode,
      password,
      programCode,
    });

    return result;
  },

  adminLogin: async ({
    adminId,
    password,
  }: {
    adminId: string;
    password: string;
  }): Promise<GenericApiResponse<AdminLoginResponse> | null> => {
    const result = await makeRequest<AdminLoginResponse>(Constants.ADMIN_LOGIN_URL, {
      adminId,
      password,
    });

    if (result && isApiResponseSuccess(result)) {
      localStorage.setItem('sessionId', result.data.sessionId);
    }
    if (result && result.data) {
      localStorage.setItem('session-id', result.data.sessionId);
    }
    return result;
  },

  getGrades: async (): Promise<GenericApiResponse<GradesResponse> | null> => {
    return makeRequest<GradesResponse>(Constants.GET_GRADES_URL);
  },

  getStudents: async ({
    gradeId,
  }: {
    gradeId: string;
  }): Promise<GenericApiResponse<StudentResponse> | null> => {
    return makeRequest<StudentResponse>(Constants.GET_STUDENTS_URL, { gradeId });
  },

  setStudentPassword: async ({
    studentId,
    password,
  }: {
    studentId: string;
    password: string;
  }): Promise<GenericApiResponse<SetStudentPasswordResponse> | null> => {
    const result = await makeRequest<SetStudentPasswordResponse>(
      Constants.SET_STUDENT_PASSWORD_URL,
      {
        studentId,
        password,
      },
    );

    if (result && isApiResponseSuccess(result)) {
      localStorage.setItem('sessionId', result.data.sessionId);
    }

    return result;
  },

  checkPassword: async ({
    studentId,
  }: {
    studentId: string;
  }): Promise<GenericApiResponse<CheckPasswordResponse> | null> => {
    return makeRequest<CheckPasswordResponse>(Constants.CHECK_PASSWORD_URL, { studentId });
  },

  studentLogin: async ({
    studentId,
    password,
  }: {
    studentId: string;
    password: string;
  }): Promise<GenericApiResponse<StudentLoginResponse> | null> => {
    return makeRequest<StudentLoginResponse>(Constants.LOGIN_STUDENT_URL, { studentId, password });
  },

  getResetPasswordToken: async ({
    studentId,
    studentCode,
  }: {
    studentId: string;
    studentCode: string;
  }): Promise<GenericApiResponse<ResetPasswordTokenResponse> | null> => {
    return makeRequest<ResetPasswordTokenResponse>(Constants.GET_RESET_PASSWORD_TOKEN_URL, {
      studentId,
      studentCode,
    });
  },

  resetPassword: async ({
    resetPasswordToken,
    password,
    studentId,
  }: {
    resetPasswordToken: string;
    password: string;
    studentId: string;
  }): Promise<GenericApiResponse<ResetPasswordResponse> | null> => {
    return makeRequest<ResetPasswordResponse>(Constants.RESET_PASSWORD_URL, {
      resetPasswordToken,
      password,
      studentId,
    });
  },

  adminLogout: async (): Promise<GenericApiResponse<AdminLogoutResponse> | null> => {
    const sessionId = getSessionItem('sessionId');
    const adminId = localStorage.getItem('adminId');
    const headers: Record<string, string> = {};
    if (sessionId) {
      headers['session-id'] = sessionId as string;
    }
    if (adminId) {
      headers['admin-id'] = adminId;
    }

    return makeRequest<AdminLogoutResponse>(Constants.ADMIN_LOGOUT_URL, {}, headers);
  },

  getSubjects: async () => {
    const accessToken = localStorage.getItem('accessToken');
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'session-id': getSessionItem('sessionId'),
      'student-id': getSessionItem('studentId'),
    };
    const result = await makeRequest<SubjectResponse>(Constants.GET_SUBJECT_LIST_URL, {}, headers);
    return result;
  },

  getInstructions: async ({ assessmentId }: { assessmentId: string }) => {
    const headers = {
      'session-id': getSessionItem('sessionId'),
      'student-id': getSessionItem('studentId'),
    };
    const parameters: Record<string, unknown> = {
      assessmentId,
    };
    const result = await makeRequest<InstructionsResponse>(
      Constants.GET_INSTRUCTIONS_URL,
      parameters,
      headers,
    );
    return result;
  },

  checkAvailability: async ({ subjectId }: { subjectId: string }) => {
    const headers = {
      'session-id': getSessionItem('sessionId'),
      'student-id': getSessionItem('studentId'),
    };
    const parameters: Record<string, unknown> = {
      subjectId,
    };
    const result = await makeRequest<CheckAvailabilityResponse>(
      Constants.CHECK_AVAILABILITY_URL,
      parameters,
      headers,
    );
    return result;
  },

  initiateTest: async ({
    accessCode,
    assessmentId,
  }: {
    assessmentId: string;
    accessCode: number;
  }) => {
    const headers = {
      'session-id': getSessionItem('sessionId'),
      'student-id': getSessionItem('studentId'),
    };
    const parameters: Record<string, unknown> = {
      accessCode,
      assessmentId,
    };
    const result = await makeRequest<InitiateTestResponse>(
      Constants.INITIATE_TEST_URL,
      parameters,
      headers,
    );
    return result;
  },

  rejoinTest: async ({
    accessCode,
    assessmentId,
    attemptId,
  }: {
    accessCode: number;
    assessmentId: string;
    attemptId: string;
  }) => {
    const headers = {
      'session-id': getSessionItem('sessionId'),
      'student-id': getSessionItem('studentId'),
    };
    const parameters: Record<string, unknown> = {
      accessCode,
      assessmentId,
      attemptId,
    };
    const result = await makeRequest<RejoinResponse>(
      Constants.REJOIN_TEST_URL,
      parameters,
      headers,
    );
    return result;
  },

  submitQuestion: async ({
    assessmentId,
    attemptId,
    questionId,
    userResponse,
    duration,
  }: {
    assessmentId: string;
    attemptId: string;
    questionId: string;
    userResponse: number;
    duration: number;
  }) => {
    const headers = {
      'session-id': getSessionItem('sessionId'),
      'student-id': getSessionItem('studentId'),
    };
    const parameters: Record<string, unknown> = {
      assessmentId,
      attemptId,
      questionId,
      userResponse,
      duration,
    };
    const result = await makeRequest<SubmitResponse>(
      Constants.SUBMIT_RESPONSE_URL,
      parameters,
      headers,
    );
    return result;
  },

  submitAssessment: async ({
    assessmentId,
    attemptId,
  }: {
    assessmentId: string;
    attemptId: string;
  }) => {
    const headers = {
      'session-id': getSessionItem('sessionId'),
      'student-id': getSessionItem('studentId'),
    };
    const parameters: Record<string, unknown> = {
      assessmentId,
      attemptId,
    };
    const result = await makeRequest<AssesementSubmitResponse>(
      Constants.SUBMIT_ASSESSMENT_URL,
      parameters,
      headers,
    );
    return result;
  },

  abortAssessment: async ({
    abortCode,
    assessmentId,
    attemptId,
  }: {
    abortCode: number;
    assessmentId: string;
    attemptId: string;
  }) => {
    const headers = {
      'session-id': getSessionItem('sessionId'),
      'student-id': getSessionItem('studentId'),
    };
    const parameters: Record<string, unknown> = {
      abortCode,
      assessmentId,
      attemptId,
    };
    const result = await makeRequest<AbortTestResponse>(
      Constants.ABORT_ASSESSMENT_URL,
      parameters,
      headers,
    );
    return result;
  },

  getLevelList: async ({ subjectId }: { subjectId: string }) => {
    const headers = {
      'session-id': getSessionItem('sessionId'),
      'student-id': getSessionItem('studentId'),
    };
    const parameters: Record<string, unknown> = {
      subjectId,
    };
    const result = await makeRequest<GetLevelListResponse>(
      Constants.GET_LEVEL_LIST_URL,
      parameters,
      headers,
    );
    return result;
  },

  getMissionList: async ({ levelId }: { levelId: string }) => {
    const headers = {
      'session-id': getSessionItem('sessionId'),
      'student-id': getSessionItem('studentId'),
    };
    const parameters: Record<string, unknown> = {
      levelId,
    };
    const result = await makeRequest<GetMissionListResponse>(
      Constants.GET_MISSION_LIST_URL,
      parameters,
      headers,
    );
    return result;
  },

  getStageList: async ({ missionId }: { missionId: string }) => {
    const headers = {
      'session-id': getSessionItem('sessionId'),
      'student-id': getSessionItem('studentId'),
    };
    const parameters: Record<string, unknown> = {
      missionId,
    };
    const result = await makeRequest<GetStageListResponse>(
      Constants.GET_STAGE_LIST_URL,
      parameters,
      headers,
    );
    return result;
  },

  getModuleList: async ({ stageId }: { stageId: string }) => {
    const headers = {
      'session-id': getSessionItem('sessionId'),
      'student-id': getSessionItem('studentId'),
    };
    const parameters: Record<string, unknown> = {
      stageId,
    };
    const result = await makeRequest<GetModuleListResponse>(
      Constants.GET_MODULE_LIST_URL,
      parameters,
      headers,
    );
    return result;
  },
};

export default apiClient;
